import cx from 'classnames';
import { forwardRef, memo, useImperativeHandle, useMemo, useState } from 'react';
import { EntityTypes } from '@wg/wows-entities/const';
import WoWSEntity from '@wg/wows-entities/wrappers/react/WoWSEntity';
import { Button, Spinner } from '@wg/wows-react-uikit';

import { localizedDateTime } from '~/helpers/internalization';
import { t } from '~/helpers/localization';

import { currencyNamesToWE } from '~/UIKit/Currency/Currency';

import styles from './TaskRow.scss';
import TaskRowBase, { renderCheckedSpan } from './TaskRowBase';

import { TASK_DESCRIPTIONS } from '../constants';

import type { TaskDescription } from '../constants';
import type { ClanTask } from '~/Actions/ActionClanTasks';

interface TaskRowHandle {
  animate: () => void;
}

interface TaskRowProps {
  isClaiming?: boolean;
  task: ClanTask;
  canClaimRewards?: boolean;
  onClaim?: () => void;
}

const TaskRow = forwardRef<TaskRowHandle, TaskRowProps>(
  ({ isClaiming = false, task, canClaimRewards = false, onClaim = () => {} }, ref) => {
    const [animationTick, setAnimationTick] = useState(0);

    useImperativeHandle(
      ref,
      () => ({
        animate: () => setAnimationTick((v) => v + 1),
      }),
      [],
    );

    const taskDescription = useMemo(() => {
      if (!(task.name in TASK_DESCRIPTIONS)) {
        return null;
      }
      return TASK_DESCRIPTIONS[task.name] as TaskDescription;
    }, [task.name]);

    const taskIconTooltip = useMemo(() => {
      if (!task.isFinished) {
        return t('Задача не выполнена');
      }

      const finishedTill = task.finishedTill
        ? t('Задача выполнена до %(date)s', { date: localizedDateTime(new Date(task.finishedTill)) })
        : t('Задача выполнена');

      return renderCheckedSpan(finishedTill);
    }, [task.finishedTill, task.isFinished]);

    const rewardId = task.reward?.oil ? currencyNamesToWE['oil'] : currencyNamesToWE['coal'];
    const rewardAmount = task.reward?.oil ? task.reward.oil : task.reward?.coal;

    const rewardReceiptDate = useMemo(() => {
      return task.isClaimed && task.claimedAt ? localizedDateTime(new Date(task.claimedAt)) : '';
    }, [task.claimedAt, task.isClaimed]);

    if (!taskDescription) {
      return null;
    }

    return (
      <TaskRowBase
        {...task}
        title={taskDescription.title}
        description={taskDescription.description}
        binary={taskDescription.binary}
        total={taskDescription.total}
        taskIconTooltip={taskIconTooltip}
        animated={animationTick > 0}
        ref={ref as React.Ref<HTMLDivElement>}
      >
        <WoWSEntity
          type={EntityTypes.CURRENCY}
          id={rewardId}
          amount={rewardAmount}
          presentation={{
            bindTooltip: true,
            withTooltip: true,
            hideReceiveText: true,
            hideSpendText: true,
          }}
          customisation={{
            rewardReceiptDate,
          }}
        >
          <div key={animationTick} className={styles.rewardResourceWrapper}>
            {task.isClaimed ? <span className={cx(styles.checkIcon, styles.rewardHasBeenClaimed)} /> : null}

            {task.reward?.coal ? (
              <span className={cx(styles.coal, { [styles.finished]: task.isFinished })}>{task.reward.coal}</span>
            ) : null}

            {task.reward?.oil ? (
              <span className={cx(styles.oil, { [styles.finished]: task.isFinished })}>{task.reward.oil}</span>
            ) : null}
          </div>

          {task.isFinished && !task.isClaimed && canClaimRewards ? (
            <div className={styles.rewardButtonWrapper}>
              {isClaiming ? (
                <Spinner label="" />
              ) : (
                <Button isFlat isSmaller onClick={onClaim}>
                  {t('Забрать')}
                </Button>
              )}
            </div>
          ) : null}
        </WoWSEntity>
      </TaskRowBase>
    );
  },
);

TaskRow.displayName = 'TaskRow';

export default memo(TaskRow);
